import { useCallback, useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import {
	ModalButtonColor,
	ModalType,
	PedOverlaySection,
	WalletType,
} from '~/shared';
import {
	setActiveListIndex,
	useLayoutDispatch,
	useLayoutSelector,
} from '../redux';

const useBuy = () => {
	const dispatch = useLayoutDispatch();
	const activeList = useLayoutSelector((state) => state.barberShop.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.barberShop.activeListIndex,
	);
	const primaryColor = useLayoutSelector(
		(state) => state.barberShop.primaryColor,
	);
	const secondaryColor = useLayoutSelector(
		(state) => state.barberShop.secondaryColor,
	);
	const hairColors = useObservable(rage.barberShop.getHairColors());
	const makeupColors = useObservable(rage.barberShop.getMakeupColors());
	const opacity = useLayoutSelector((state) => state.barberShop.opacity);
	const currentAppearance = useObservable(
		rage.barberShop.getCurrentAppearance(),
	);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const item = useMemo(
		() => activeList[activeListIndex || 0],
		[activeList, activeListIndex],
	);

	const handleBuy = useCallback(
		(walletType: WalletType) => {
			if (modalState) {
				return;
			}

			const isHair =
				item.overlaySection === PedOverlaySection.Hair ||
				item.overlaySection === PedOverlaySection.FacialHair ||
				item.overlaySection === PedOverlaySection.Eyebrows ||
				item.overlaySection === PedOverlaySection.BodyHair;
			const palette = isHair ? hairColors : makeupColors;
			const primaryColorIndex =
				palette?.find((c) => c.hex === primaryColor)?.index ?? 0;
			const secondaryColorIndex =
				palette?.find((c) => c.hex === secondaryColor)?.index ?? 0;

			const installedOverlay = currentAppearance?.overlays.find(
				(o) => o.section === item.overlaySection,
			);
			const isSameItem = installedOverlay?.index === item.index;
			const isSamePrimary =
				installedOverlay?.colorPrimary === primaryColorIndex;
			const isSameSecondary =
				installedOverlay?.colorSecondary === secondaryColorIndex;
			const isSameOpacity = installedOverlay?.colorOpacity === opacity;

			if (isSameItem && isSamePrimary && isSameSecondary && isSameOpacity) {
				rage.notifications.showInfo('Этот предмет уже установлен');
				return;
			}
			const priceToShow =
				isSameItem && isSamePrimary && isSameSecondary && isSameOpacity
					? 0
					: item.price;

			const text = `Вы действительно хотите оплатить ${
				walletType === WalletType.Cash ? 'наличными' : 'картой'
			} товар "${item.name}"`;

			rage.modal.showModal(
				{
					type: ModalType.Price,
					title: 'Подтверждение',
					text: text,
					price: priceToShow,
					wallet: walletType,
					acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
					cancelButton: {
						text: 'Отменить',
						color: ModalButtonColor.Secondary,
					},
				},
				(result) => {
					if (result.isAccepted) {
						rage.barberShop.buyItem(
							item.overlaySection!,
							item.index!,
							primaryColorIndex,
							secondaryColorIndex,
							opacity,
							walletType,
						);
						dispatch(setActiveListIndex(null));
					}
				},
			);
		},
		[item, primaryColor, secondaryColor, opacity, modalState],
	);

	return { handleBuy };
};

export { useBuy };
